import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Plus, Package, Edit, Trash2, Users, X, Tv, FolderTree } from 'lucide-react'

interface Bouquet {
  id: string
  name: string
  description?: string
  stream_ids?: string[]
  category_ids?: string[]
  is_active: boolean
  user_count?: number
}

interface Stream {
  id: string
  name: string
  type: string
}

interface Category {
  id: string
  name: string
  type: string
}

interface BouquetFormData {
  name: string
  description: string
  stream_ids: string[]
  category_ids: string[]
  is_active: boolean
}

const defaultFormData: BouquetFormData = {
  name: '',
  description: '',
  stream_ids: [],
  category_ids: [],
  is_active: true,
}

export default function Bouquets() {
  const [modalOpen, setModalOpen] = useState(false)
  const [editingBouquet, setEditingBouquet] = useState<Bouquet | null>(null)
  const [formData, setFormData] = useState<BouquetFormData>(defaultFormData)
  const [error, setError] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['bouquets'],
    queryFn: () => api.getBouquets(),
  })

  // Fetch all streams for selection
  const { data: streamsData } = useQuery({
    queryKey: ['streams-all'],
    queryFn: () => api.getStreams({ per_page: 1000 }),
  })

  // Fetch all categories for selection
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-all'],
    queryFn: () => api.getCategories({}),
  })

  const createMutation = useMutation({
    mutationFn: (data: BouquetFormData) => api.createBouquet({
      name: data.name,
      description: data.description || undefined,
      stream_ids: data.stream_ids,
      category_ids: data.category_ids,
      is_active: data.is_active,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['bouquets'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create bouquet')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: BouquetFormData }) =>
      api.updateBouquet(id, {
        name: data.name,
        description: data.description || undefined,
        stream_ids: data.stream_ids,
        category_ids: data.category_ids,
        is_active: data.is_active,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['bouquets'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update bouquet')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteBouquet(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['bouquets'] })
    },
  })

  const openCreateModal = () => {
    setEditingBouquet(null)
    setFormData(defaultFormData)
    setError('')
    setModalOpen(true)
  }

  const openEditModal = (bouquet: Bouquet) => {
    setEditingBouquet(bouquet)
    setFormData({
      name: bouquet.name,
      description: bouquet.description || '',
      stream_ids: bouquet.stream_ids || [],
      category_ids: bouquet.category_ids || [],
      is_active: bouquet.is_active,
    })
    setError('')
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingBouquet(null)
    setFormData(defaultFormData)
    setError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (editingBouquet) {
      updateMutation.mutate({ id: editingBouquet.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (bouquet: Bouquet) => {
    if (confirm(`Delete bouquet "${bouquet.name}"?`)) {
      deleteMutation.mutate(bouquet.id)
    }
  }

  const toggleStream = (streamId: string) => {
    setFormData(prev => ({
      ...prev,
      stream_ids: prev.stream_ids.includes(streamId)
        ? prev.stream_ids.filter(id => id !== streamId)
        : [...prev.stream_ids, streamId]
    }))
  }

  const toggleCategory = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }))
  }

  const bouquets = data?.bouquets || []
  const streams: Stream[] = streamsData?.streams || []
  const categories: Category[] = categoriesData || []

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Bouquets</h1>
        <button onClick={openCreateModal} className="btn-primary shrink-0">
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Bouquet</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Bouquets Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 animate-pulse">
              <div className="h-12 w-12 bg-muted rounded-lg mb-4"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          ))
        ) : bouquets.length > 0 ? (
          bouquets.map((bouquet: Bouquet) => (
            <div
              key={bouquet.id}
              className="bg-card rounded-lg border border-border p-6 hover:border-muted-foreground/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Package className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-foreground">{bouquet.name}</h3>
                    {bouquet.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {bouquet.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEditModal(bouquet)}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(bouquet)}
                    className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                  <div className="flex items-center gap-1">
                    <Tv className="w-4 h-4" />
                    {bouquet.stream_ids?.length || 0} streams
                  </div>
                  <div className="flex items-center gap-1">
                    <FolderTree className="w-4 h-4" />
                    {bouquet.category_ids?.length || 0} categories
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="w-4 h-4" />
                    {bouquet.user_count || 0} users
                  </div>
                  <span className={bouquet.is_active ? 'badge-success' : 'badge-destructive'}>
                    {bouquet.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-base text-muted-foreground">
            No bouquets found
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-card rounded-lg border border-border p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                {editingBouquet ? 'Edit Bouquet' : 'Add Bouquet'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-base">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="form-label">Description (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field"
                  rows={2}
                />
              </div>

              {/* Categories Selection */}
              <div>
                <label className="form-label">
                  Categories ({formData.category_ids.length} selected)
                  <span className="text-muted-foreground font-normal ml-2">- All streams in selected categories are included</span>
                </label>
                <div className="border border-border rounded-lg p-3 max-h-40 overflow-y-auto bg-background">
                  {categories.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {categories.map((category: Category) => (
                        <label
                          key={category.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.category_ids.includes(category.id)}
                            onChange={() => toggleCategory(category.id)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-foreground">{category.name}</span>
                          <span className="text-xs text-muted-foreground capitalize">({category.type})</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No categories available</p>
                  )}
                </div>
              </div>

              {/* Streams Selection */}
              <div>
                <label className="form-label">
                  Individual Streams ({formData.stream_ids.length} selected)
                  <span className="text-muted-foreground font-normal ml-2">- Add specific streams</span>
                </label>
                <div className="border border-border rounded-lg p-3 max-h-48 overflow-y-auto bg-background">
                  {streams.length > 0 ? (
                    <div className="space-y-1">
                      {streams.map((stream: Stream) => (
                        <label
                          key={stream.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.stream_ids.includes(stream.id)}
                            onChange={() => toggleStream(stream.id)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-foreground">{stream.name}</span>
                          <span className="text-xs text-muted-foreground capitalize">({stream.type})</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No streams available</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="bouquet_is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="bouquet_is_active" className="text-base text-foreground">Active</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingBouquet
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
